import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { ExportButton } from '@/app/ExportButton';
import { formatMoney, isNegative } from '@/lib/money';
import type { MoneyString } from '@/lib/money';

type Row = {
  key: string;
  label: string;
  income: MoneyString;
  expense: MoneyString;
  result: MoneyString;
  margin_percent: number | null;
  result_delta: MoneyString | null;
};

type Comparison = {
  granularity: 'month' | 'year';
  rows: Row[];
  totals: {
    income: MoneyString;
    expense: MoneyString;
    result: MoneyString;
    average_income: MoneyString;
    average_expense: MoneyString;
    best: { label: string; result: MoneyString } | null;
    worst: { label: string; result: MoneyString } | null;
  };
};

/**
 * Comparação entre períodos — RF-REL-10.
 *
 * O DRE diz quanto foi agosto. Esta tela diz se agosto foi bom — ao lado de
 * julho, de junho e da média. Tendência só existe em série; um mês isolado não
 * tem tendência nenhuma.
 *
 * NÃO segue o período da barra superior, e diz isso: uma série de 12 meses
 * dentro de um recorte de um mês não seria série.
 */
export function ComparisonReport({ selectedIds }: { selectedIds: number[] }) {
  const [granularity, setGranularity] = useState<'month' | 'year'>('month');
  const [periods, setPeriods] = useState(12);

  const query = useQuery({
    queryKey: ['report-comparison', granularity, periods, selectedIds],
    queryFn: () =>
      api.get<{ data: Comparison }>(
        `/reports/comparison?granularity=${granularity}&periods=${periods}`,
        { entityIds: selectedIds },
      ),
    select: (r) => r.data,
  });

  const d = query.data;

  // Escala do gráfico: o maior valor absoluto da série define a altura das
  // barras. Sem base comum, meses não seriam comparáveis entre si.
  const maxValue = Math.max(
    1,
    ...(d?.rows ?? []).flatMap((r) => [Math.abs(Number(r.income)), Math.abs(Number(r.expense))]),
  );

  return (
    <div className="mt-4">
      <div className="flex flex-wrap items-center gap-2.5">
        <div className="order-last ml-auto">
          <ExportButton
            path="/reports/comparison/export"
            query={{ granularity, periods }}
            entityIds={selectedIds}
            disabled={query.isPending}
          />
        </div>

        <div className="flex rounded-lg border border-slate-300 p-0.5 dark:border-slate-700">
          {([['month', 'Mês a mês'], ['year', 'Ano a ano']] as const).map(([id, label]) => (
            <button
              key={id}
              onClick={() => {
                setGranularity(id);
                setPeriods(id === 'year' ? 5 : 12);
              }}
              className={`rounded-md px-3 py-1.5 text-sm font-medium transition ${
                granularity === id
                  ? 'bg-indigo-600 text-white'
                  : 'text-slate-600 hover:text-slate-900 dark:text-slate-400'
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        <select
          value={periods}
          onChange={(e) => setPeriods(Number(e.target.value))}
          className="input py-1.5 text-sm"
        >
          {(granularity === 'year' ? [3, 5, 10] : [6, 12, 24]).map((n) => (
            <option key={n} value={n}>
              últimos {n} {granularity === 'year' ? 'anos' : 'meses'}
            </option>
          ))}
        </select>

        {/* A ausência do filtro global aqui é intencional e precisa ser dita. */}
        <span className="text-xs text-slate-400">
          não segue o período da barra — a série tem o próprio recorte
        </span>
      </div>

      {query.isPending ? (
        <div className="mt-4 h-64 animate-pulse rounded-xl bg-slate-100 dark:bg-slate-800" />
      ) : query.isError || !d ? (
        <p className="mt-4 rounded-xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-800 dark:border-rose-900/50 dark:bg-rose-950/30 dark:text-rose-300">
          Não foi possível gerar a comparação.
        </p>
      ) : (
        <>
          {/* ---------- Resumo da série ---------- */}
          <div className="mt-4 grid grid-cols-2 gap-3 lg:grid-cols-4">
            <Card label="Receita média" value={d.totals.average_income} tone="receita" />
            <Card label="Despesa média" value={d.totals.average_expense} tone="despesa" />
            <Card label="Resultado do período" value={d.totals.result} signed />
            <div className="rounded-xl border border-slate-200 bg-white p-3.5 dark:border-slate-800 dark:bg-slate-900">
              <p className="text-xs uppercase tracking-wide text-slate-400">Melhor e pior</p>
              <p className="mt-1 text-sm">
                <span className="text-emerald-600 dark:text-emerald-400">
                  ▲ {d.totals.best?.label ?? '—'}
                </span>
                {' · '}
                <span className="text-rose-600 dark:text-rose-400">
                  ▼ {d.totals.worst?.label ?? '—'}
                </span>
              </p>
              <p className="mt-0.5 text-[11px] text-slate-400">por resultado, não por faturamento</p>
            </div>
          </div>

          {/* ---------- Gráfico ---------- */}
          <div className="mt-3 overflow-x-auto rounded-xl border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-900">
            <div className="flex min-w-fit items-end gap-3" style={{ height: '11rem' }}>
              {d.rows.map((row) => {
                const inH = (Math.abs(Number(row.income)) / maxValue) * 100;
                const outH = (Math.abs(Number(row.expense)) / maxValue) * 100;

                return (
                  <div key={row.key} className="flex w-14 shrink-0 flex-col items-center gap-1">
                    <div className="flex h-full w-full items-end justify-center gap-0.5">
                      <div
                        className="w-1/2 rounded-t bg-emerald-500/80"
                        style={{ height: `${inH}%` }}
                        title={`Receita ${formatMoney(row.income)}`}
                      />
                      <div
                        className="w-1/2 rounded-t bg-rose-500/80"
                        style={{ height: `${outH}%` }}
                        title={`Despesa ${formatMoney(row.expense)}`}
                      />
                    </div>
                    <span className="text-[10px] text-slate-500 dark:text-slate-400">{row.label}</span>
                  </div>
                );
              })}
            </div>

            <div className="mt-3 flex gap-4 text-xs text-slate-400">
              <span><span className="mr-1 inline-block h-2 w-2 rounded-sm bg-emerald-500/80" />Receita</span>
              <span><span className="mr-1 inline-block h-2 w-2 rounded-sm bg-rose-500/80" />Despesa</span>
            </div>
          </div>

          {/* ---------- Tabela ---------- */}
          <div className="mt-3 overflow-x-auto rounded-xl border border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-900">
            <table className="w-full text-sm">
              <thead className="border-b border-slate-200 text-left text-xs uppercase tracking-wide text-slate-500 dark:border-slate-800 dark:text-slate-400">
                <tr>
                  <th className="px-3 py-2.5 font-medium">Período</th>
                  <th className="px-3 py-2.5 text-right font-medium">Receita</th>
                  <th className="px-3 py-2.5 text-right font-medium">Despesa</th>
                  <th className="px-3 py-2.5 text-right font-medium">Resultado</th>
                  <th className="px-3 py-2.5 text-right font-medium">Margem</th>
                  <th className="px-3 py-2.5 text-right font-medium">vs. anterior</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                {d.rows.map((row) => (
                  <tr key={row.key} className="hover:bg-slate-50 dark:hover:bg-slate-800/50">
                    <td className="px-3 py-2.5 font-medium">{row.label}</td>
                    <td className="whitespace-nowrap px-3 py-2.5 text-right tabular-nums text-emerald-600 dark:text-emerald-400">
                      {formatMoney(row.income)}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2.5 text-right tabular-nums text-rose-600 dark:text-rose-400">
                      {formatMoney(row.expense)}
                    </td>
                    <td
                      className={`whitespace-nowrap px-3 py-2.5 text-right font-medium tabular-nums ${
                        isNegative(row.result) ? 'text-rose-600 dark:text-rose-400' : ''
                      }`}
                    >
                      {formatMoney(row.result)}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2.5 text-right tabular-nums text-slate-500 dark:text-slate-400">
                      {/* Margem responde o que o resultado em reais não responde:
                          dois meses com o mesmo lucro são diferentes se um
                          faturou o dobro. */}
                      {row.margin_percent === null ? '—' : `${row.margin_percent}%`}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2.5 text-right tabular-nums">
                      {row.result_delta === null ? (
                        <span className="text-xs text-slate-400">—</span>
                      ) : (
                        <span
                          className={`text-xs font-medium ${
                            isNegative(row.result_delta)
                              ? 'text-rose-600 dark:text-rose-400'
                              : 'text-emerald-600 dark:text-emerald-400'
                          }`}
                        >
                          {isNegative(row.result_delta) ? '▼' : '▲'}{' '}
                          {formatMoney(row.result_delta.replace('-', ''))}
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

function Card({
  label,
  value,
  tone,
  signed,
}: {
  label: string;
  value: MoneyString;
  tone?: 'receita' | 'despesa';
  signed?: boolean;
}) {
  const cor =
    tone === 'receita'
      ? 'text-emerald-600 dark:text-emerald-400'
      : tone === 'despesa'
        ? 'text-rose-600 dark:text-rose-400'
        : signed && isNegative(value)
          ? 'text-rose-600 dark:text-rose-400'
          : '';

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-3.5 dark:border-slate-800 dark:bg-slate-900">
      <p className="text-xs uppercase tracking-wide text-slate-400">{label}</p>
      <p className={`mt-1 text-lg font-semibold ${cor}`}>{formatMoney(value)}</p>
    </div>
  );
}
